import React, { useState, useEffect, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchHomeBanners, trackBannerClick } from '@/services/homeBanners';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { isInternalUrl, getInternalPath } from '@/utils/internalUrl';
import { prefetchPageComponent, prefetchRouteData } from '@/hooks/useComponentPrefetch';
import { useQueryClient } from '@tanstack/react-query';

export function HomeBannerCarousel() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Derive toolId from internal URL for prefetch
  const getToolId = (url: string) => {
    const path = getInternalPath(url);
    const segment = path.replace(/^\//, '').split('/')[0];
    return segment || null;
  };

  const handlePrefetch = (url: string | null) => {
    if (!url || !isInternalUrl(url)) return;
    const toolId = getToolId(url);
    if (toolId) {
      prefetchPageComponent(toolId);
      prefetchRouteData(toolId, queryClient);
    }
  };
  const { data: banners = [], isLoading } = useQuery({
    queryKey: ['home-banners'],
    queryFn: fetchHomeBanners,
    staleTime: 5 * 60 * 1000,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);
  const rotateMs = (banners[0]?.auto_rotate_seconds ?? 5) * 1000;
  const goTo = useCallback((i: number) => setSelectedIndex(i), []);

  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(() => {
      setSelectedIndex(prev => (prev + 1) % banners.length);
    }, rotateMs);
    return () => clearInterval(interval);
  }, [banners.length, rotateMs]);

  if (isLoading || banners.length === 0) {
    return (
      <div className="h-full flex flex-col">
        <div className="md:hidden rounded-xl overflow-hidden border border-border/20">
          <Skeleton className="aspect-[4/5] w-full" />
        </div>
        <div className="hidden md:block rounded-2xl overflow-hidden">
          <Skeleton className="w-full" style={{ aspectRatio: '1550/200' }} />
        </div>
      </div>
    );
  }

  const handleLinkClick = (e: React.MouseEvent, url: string, bannerId: string) => {
    trackBannerClick(bannerId);
    if (isInternalUrl(url)) {
      e.preventDefault();
      navigate(getInternalPath(url));
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Mobile fade carousel */}
      <div className="md:hidden relative rounded-xl overflow-hidden border border-white/20 shadow-[0_0_20px_rgba(255,255,255,0.06)] ring-1 ring-white/[0.08]">
        <div className="relative aspect-[4/5]">
          {banners.map((banner, i) => {
            const buttonColorClass = banner.button_color === 'black'
              ? 'bg-black text-white hover:bg-black/90'
              : banner.button_color === 'white'
              ? 'bg-white text-black hover:bg-white/90'
              : 'bg-[hsl(32,95%,55%)] text-black hover:bg-[hsl(32,95%,50%)]';
            const isActive = i === selectedIndex;
            const isInternal = banner.link_url ? isInternalUrl(banner.link_url) : false;
            const linkTarget = isInternal ? '_self' : '_blank';

            return (
              <div
                key={banner.id}
                className={cn(
                  "absolute inset-0 transition-opacity duration-300 ease-in-out",
                  isActive ? "opacity-100 z-10" : "opacity-0 z-0"
                )}
              >
                {banner.link_url ? (
                  <a
                    href={isInternal ? getInternalPath(banner.link_url) : banner.link_url}
                    target={linkTarget}
                    rel="noopener noreferrer"
                    className="block h-full"
                    onTouchStart={() => handlePrefetch(banner.link_url)}
                    onClick={(e) => handleLinkClick(e, banner.link_url!, banner.id)}
                  >
                    <img src={banner.mobile_image_url} alt={banner.button_label || "Banner promocional ForjaHub"} className="w-full h-full object-cover" loading={isActive ? "eager" : "lazy"} />
                  </a>
                ) : (
                  <img src={banner.mobile_image_url} alt={banner.button_label || "Banner promocional ForjaHub"} className="w-full h-full object-cover" loading={isActive ? "eager" : "lazy"} />
                )}
                {banner.link_url && (
                  <a
                    href={isInternal ? getInternalPath(banner.link_url) : banner.link_url}
                    target={linkTarget}
                    rel="noopener noreferrer"
                    onTouchStart={() => handlePrefetch(banner.link_url)}
                    onClick={(e) => handleLinkClick(e, banner.link_url!, banner.id)}
                    className={cn(
                      "absolute bottom-3 left-4 right-4 py-2 rounded-lg text-sm font-medium text-center shadow-lg transition-colors",
                      buttonColorClass
                    )}
                  >
                    {banner.button_label || 'Saiba mais'}
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Desktop fade carousel */}
      <div className="hidden md:block relative rounded-2xl overflow-hidden">
        <div className="relative" style={{ aspectRatio: '1550/200' }}>
          {banners.map((banner, i) => {
            const isActive = i === selectedIndex;
            const isInternal = banner.link_url ? isInternalUrl(banner.link_url) : false;
            const linkTarget = isInternal ? '_self' : '_blank';

            return (
              <div
                key={banner.id}
                className={cn(
                  "absolute inset-0 transition-opacity duration-300 ease-in-out",
                  isActive ? "opacity-100 z-10" : "opacity-0 z-0"
                )}
              >
                <img src={banner.desktop_image_url} alt={banner.button_label || "Banner promocional ForjaHub"} className="w-full h-full object-cover" loading={isActive ? "eager" : "lazy"} />
                {banner.link_url && (
                  <a
                    href={isInternal ? getInternalPath(banner.link_url) : banner.link_url}
                    target={linkTarget}
                    rel="noopener noreferrer"
                    className="absolute bottom-3 left-4"
                    onMouseEnter={() => handlePrefetch(banner.link_url)}
                    onClick={(e) => handleLinkClick(e, banner.link_url!, banner.id)}
                  >
                    <Button size="sm" className="gap-1.5 text-xs shadow-lg">
                      {banner.button_label || 'Saiba mais'}
                      <ArrowRight className="w-3.5 h-3.5" />
                    </Button>
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {banners.length > 1 && (
        <div className="flex justify-center gap-1.5 mt-2">
          {banners.map((_, i) => (
            <button
              key={i}
              className={cn(
                "w-1.5 h-1.5 rounded-full transition-all",
                i === selectedIndex ? "bg-primary w-4" : "bg-muted-foreground/30"
              )}
              onClick={() => goTo(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
